/**
 * Basic config for which requests can be cancelled when called in quick succession.
 * The comparison just checks if the current URL includes this list of strings.
 */
const CANCELLABLE_URLS = [
  'AvailabilitySearch?costPerNight=true',
];

// Standard error message constants, can be used to pick up on a cancellation
// error elsewhere, e.g. in AvailabilitySearchQuery
export const ABORT_REASON = 'AbortError';
export const ABORT_ERROR_MESSAGE = `Network error: ${ABORT_REASON}`;

/**
 * SSR-safe abort controller handling
 * @returns {AbortController}
 */
const getAbortController = () => {
  if (typeof window === 'undefined') return null;
  return new AbortController();
};

/**
 * Checks if a URL should be cancellable, based on basic
 * above configuration
 * @param {string} url
 * @returns {boolean}
 */
const shouldUrlBeCancellable = (url) => CANCELLABLE_URLS
  .some((cancellableURL) => url.includes(cancellableURL));

// Map of AbortControllers so we can use this across multiple requests at once
const abortControllers = new Map();

/**
 * Custom fetch handler passed to apollo-rest-link which enables us to add
 * request cancellation to certain requests where required
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response> | Response}
 */
const customFetch = async (url, options) => {
  const shouldThisRequestBeCancellable = shouldUrlBeCancellable(url);

  // If we don't have cancellations enabled for this URL, just return
  // a standard fetch
  if (!shouldThisRequestBeCancellable) {
    return fetch(url, options);
  }

  try {
    // Clear out params so we don't get too specific with the URL matching
    // Lots of our queries use querystring key-values to send variables
    const splitUrl = url?.split('?');
    const urlWithoutParams = splitUrl?.[0] ?? '';

    // If the request has a controller, then abort it to cancel any in-flight requests
    if (abortControllers.has(urlWithoutParams)) {
      abortControllers.get(urlWithoutParams).abort();
    }

    // Create a new AbortController and store it in the map
    const newController = getAbortController();
    abortControllers.set(urlWithoutParams, newController);

    // Attach the signal to the request so we can cancel it
    // if called again
    const response = await fetch(url, {
      ...options,
      signal: newController.signal,
    });
    return response;
  } catch (error) {
    // This just makes sure we throw the error in the format
    // components expect. i.e. the error message matches the
    // exported error message at the top of this file
    if (error.name === ABORT_REASON) {
      throw new Error(error.name);
    }
    // Re-throw any other errors for Apollo to handle normally
    throw error;
  }
};

export default customFetch;
